import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Plus, User } from "lucide-react";
import {
  Controller,
  type Control,
  type FieldPath,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

import { CoachImageUploadField } from "~/components/form/coach-image-upload-field";
import { ControlledTextarea } from "~/components/form/controlled-inputs";
import {
  FormErrorSummary,
  notifyInvalidSubmit,
} from "~/components/form/form-error-summary";
import {
  FormCheckboxField,
  FormFileInputField,
  FormIconPickerField,
  FormSelectField,
} from "~/components/form/form-fields";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import { CtaBanner } from "~/components/ui/cta-banner";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { useCoachEmbeddingUpdater } from "~/hooks/use-coach-embedding-updater";
import { cn } from "~/lib/cn";
import { fetchCalendars } from "~/lib/functions/coach-calendars";
import {
  getCoachProfile,
  updateCoachProfileAction,
  ProfileSchema,
} from "~/lib/functions/coach-profile";
import { Feature, hasFeature } from "~/lib/has-feature";
import {
  TrackingCampaign,
  TrackingMedium,
} from "~/lib/monitoring/tracking-parameters";

import { CollapsibleCard } from "./-components/collapsible-card";

type FormData = z.infer<typeof ProfileSchema>;

// Human-readable labels for the form-level error summary so a coach sees, for
// example, "CTA button text" instead of a raw field path.
const BIO_FIELD_LABELS: Record<string, string> = {
  coachingTitle: "Coaching title",
  title: "Coaching statement (lede)",
  ctaButtonText: "CTA button text",
  contactMode: "Primary CTA action",
  ghlCalendarId: "Booking calendar",
  mainVideo: "Intro video URL",
  mainImage: "Hero portrait",
  audienceFor: "Who you work with",
  typicalEngagement: "Typical engagement",
  coachingSinceYear: "Coaching since",
  description: "About — description",
  whyTitle: "Approach — title",
  whyContent: "Approach — your story",
  challengesHeadline: "Challenges — headline",
  challengesDescription: "Challenges — description",
  outcomesHeadline: "Outcomes — headline",
  outcomesDescription: "Outcomes — intro paragraph",
  openingStatement: "Opening statement (marketplace listings)",
  brandName: "Brand name",
  brandLogoUrl: "Brand logo",
  leadMagnetTitle: "Lead magnet title",
  leadMagnetDescription: "Lead magnet description",
  leadMagnetCtaText: "Lead magnet submit button text",
  leadMagnetDownloadCtaText: "Lead magnet download button text",
  leadMagnetUrl: "Lead magnet URL",
  facebookProfile: "Facebook profile",
  linkedinProfile: "LinkedIn profile",
  instagramProfile: "Instagram profile",
  youtubeChannel: "YouTube channel",
  "results.*.title": "Result · title",
  "results.*.icon": "Result · icon",
  "results.*.description": "Result · description",
};

/**
 * A run of cards that mirrors one structural block of the public profile
 * (hero, numbered sections, footer), so coaches can scan the editor and see
 * "this is the part of my profile I'm touching."
 */
function FormGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <p className="eyebrow text-purple-700">{label}</p>
        {hint && <p className="text-text-secondary text-sm">{hint}</p>}
      </header>
      {children}
    </section>
  );
}

/**
 * CTA action picker — only rendered for coaches with a connected GHL location.
 * Lets them choose whether the primary CTA opens a booking calendar or a
 * message form, and (for booking) which GHL calendar to embed.
 */
function CtaActionFields({ control }: { control: Control<FormData> }) {
  const coach = useCoachPrivateProfileRequired();
  const hasGhl = !!coach.ghlLocationId;

  const contactMode = useWatch({ control, name: "contactMode" });
  const ghlCalendarIdValue = useWatch({ control, name: "ghlCalendarId" });

  const calendarsQuery = useQuery({
    queryKey: ["calendars", coach.ghlLocationId],
    queryFn: () => fetchCalendars(),
    enabled: hasGhl && contactMode === "booking",
  });

  if (!hasGhl) return null;

  return (
    <>
      <div>
        <label htmlFor="bio-contactMode">Primary CTA action</label>
        <p className="text-text-tertiary text-sm">
          What happens when a visitor clicks your CTA button — open your
          calendar to book a call, or send you a message?
        </p>
        <Controller
          control={control}
          name="contactMode"
          render={({ field }) => (
            <Select
              value={field.value ?? "messaging"}
              onValueChange={field.onChange}
            >
              <SelectTrigger id="bio-contactMode">
                <SelectValue placeholder="Choose an action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="booking">Book a call</SelectItem>
                <SelectItem value="messaging">Send a message</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      {contactMode === "booking" && (
        <div>
          <label htmlFor="bio-ghlCalendarId">Booking calendar</label>
          <p className="text-text-tertiary text-sm">
            Which calendar should visitors land on when they click the CTA?
          </p>
          <Controller
            control={control}
            name="ghlCalendarId"
            render={({ field }) => {
              const calendarOptions = calendarsQuery.data?.calendars ?? [];
              return (
                <Select
                  value={field.value ?? "none"}
                  onValueChange={(value) =>
                    field.onChange(value === "none" ? null : value)
                  }
                  disabled={calendarsQuery.isPending}
                >
                  <SelectTrigger id="bio-ghlCalendarId">
                    <SelectValue
                      placeholder={
                        calendarsQuery.isPending
                          ? "Loading calendars..."
                          : "Select a calendar"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No calendar selected</SelectItem>
                    {calendarOptions.map((cal) => (
                      <SelectItem key={cal.id} value={cal.id}>
                        {cal.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
          {(calendarsQuery.error || calendarsQuery.data?.error) && (
            <p className="text-sm text-red-500">
              {calendarsQuery.data?.error ||
                (calendarsQuery.error instanceof Error
                  ? calendarsQuery.error.message
                  : "Failed to load calendars")}
            </p>
          )}
          {!ghlCalendarIdValue &&
            !calendarsQuery.isPending &&
            !calendarsQuery.error &&
            !calendarsQuery.data?.error && (
              <p className="text-sm text-amber-800">
                Pick a calendar so visitors can actually book a call.
              </p>
            )}
        </div>
      )}
    </>
  );
}

export const Route = createFileRoute("/_user/my-coaching/profile/bio")({
  staticData: { title: "Profile & bio" },
  loader: async () => {
    const profileData = await getCoachProfile();
    return { profileData };
  },
  component: ProfileBioPage,
});

function ProfileBioPage() {
  const queryClient = useQueryClient();
  const coach = useCoachPrivateProfileRequired();
  const { profileData } = Route.useLoaderData();
  const { refresh: refreshCoachEmbedding } = useCoachEmbeddingUpdater();

  const { register, handleSubmit, formState, control, reset } =
    useForm<FormData>({
      resolver: zodResolver(ProfileSchema),
      defaultValues: profileData,
    });

  const { errors, isDirty } = formState;

  // Card-level error check — passes a list of field names from this form and
  // returns true if any of them currently has a validation error. Drives the
  // CollapsibleCard error border + auto-open behavior.
  const hasErrorsIn = (...keys: Array<keyof FormData>) =>
    keys.some((k) => Boolean(errors[k]));

  const {
    fields: results,
    append,
    move,
  } = useFieldArray({
    control,
    name: "results",
    keyName: "fieldId",
  });

  const addResult = () => {
    append({
      _delete: false,
      id: undefined,
      title: "",
      description: "",
      icon: "award",
      tempId: `temp-${Date.now()}-${Math.random()}`,
    });
  };

  const moveResult = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= results.length) return;
    move(fromIndex, toIndex);
  };

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => updateCoachProfileAction({ data }),
    onSuccess: async (freshData) => {
      toast.success("Profile updated");
      reset(freshData);
      await refreshCoachEmbedding();
      await queryClient.invalidateQueries({ queryKey: ["coach"] });
    },
    onError: (error) => {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    },
  });

  const { showExitAlert, onDiscard, onCancel } = useUnsavedChangesAlert(
    () => isDirty,
  );

  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && Object.keys(errors).length > 0,
  );

  const onSubmit = (data: FormData) => {
    updateMutation.mutate(data);
  };

  const isPro = hasFeature(coach, Feature.FancyProfile);

  return (
    <>
      <UnsavedChangesAlert
        show={showExitAlert}
        onDiscard={onDiscard}
        onCancel={onCancel}
      />
      <PristineInvalidAlert {...pristineAlert} />
      <form
        onSubmit={handleSubmit(onSubmit, notifyInvalidSubmit)}
        className="space-y-8"
      >
        <ProfileEditHeader
          titleAs="h2"
          title="Coach profile"
          description="Cards below mirror the order of sections on your public profile, so you can see exactly which part you're editing."
          form={formState}
          isSaving={updateMutation.isPending}
          username={coach.username}
        />

        <FormErrorSummary errors={errors} labels={BIO_FIELD_LABELS} />

        <input type="hidden" {...register("id")} />
        <input type="hidden" {...register("coachId")} />

        <CollapsibleCard
          title="Profile visibility"
          description="Control where your profile appears."
        >
          <CardContent className="space-y-4">
            <FormCheckboxField control={control} name="isPublic">
              Show my profile on Dream Coach Match (for example, the Coach
              Finder and overview pages)
            </FormCheckboxField>
            <FormCheckboxField control={control} name="searchIndexingAllowed">
              Allow search engines (like Google) to index my profile
            </FormCheckboxField>
          </CardContent>
        </CollapsibleCard>

        <FormGroup
          label="Hero — top of your profile"
          hint="Your portrait, headline, primary CTA, and the small at-a-glance card."
        >
          <CollapsibleCard
            title="Hero portrait"
            description="The big portrait that sits next to your name."
            defaultOpen={false}
            hasError={hasErrorsIn("mainImage")}
          >
            <CardContent className="space-y-4">
              <CoachImageUploadField
                control={control}
                name="mainImage"
                purpose="main-image"
                label="Hero portrait"
                helpText="High-quality vertical photo with you facing forward — close enough that your expression reads at a glance. Cropped to a 4:5 portrait. If left empty, your account profile photo is used instead."
                recommendedDimensions={{ width: 1200, height: 1500 }}
                accept=".jpg,.jpeg,.png"
              />
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="Hero text & CTA"
            description="Kicker (above your name), the lede sentence, and the primary call-to-action button."
            hasError={hasErrorsIn(
              "coachingTitle",
              "title",
              "ctaButtonText",
              "contactMode",
              "ghlCalendarId",
              "mainVideo",
            )}
          >
            <CardContent className="flex flex-col gap-4 md:flex-row">
              <div className="flex flex-col items-center gap-2 md:w-1/3">
                <Avatar className="h-24 w-24 rounded-full ring-2 ring-gray-100 ring-offset-2">
                  <AvatarImage
                    src={profileData.profilePictureUrl ?? undefined}
                  />
                  <AvatarFallback className="rounded-full bg-purple-50">
                    <User size={32} className="text-purple-400" />
                  </AvatarFallback>
                </Avatar>
                <p className="text-text-tertiary text-center text-xs">
                  Your profile photo (used as a circular avatar in the About
                  section) is synced from your{" "}
                  <Link
                    to="/settings/profile"
                    className="text-brand-700 underline hover:text-brand-900 hover:no-underline"
                  >
                    account settings
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-col gap-4 md:w-2/3">
                <p className="text-base font-medium text-gray-900">
                  {coach.fullName}
                </p>
                <div>
                  <label htmlFor="bio-coachingTitle">Coaching title</label>
                  <p className="text-text-tertiary text-sm">
                    Short kicker that appears just above your name (e.g., "Life
                    Coach", "Executive Career Coach").
                  </p>
                  <Input
                    id="bio-coachingTitle"
                    {...register("coachingTitle")}
                    placeholder="Life Coach"
                  />
                  {errors.coachingTitle && (
                    <p className="text-sm text-red-500">
                      {errors.coachingTitle.message}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="bio-title">Coaching statement (lede)</label>
                  <p className="text-text-tertiary text-sm">
                    The big sentence right under your name. The "I help [who] to
                    [achieve outcome]" pattern works well.
                  </p>
                  <ControlledTextarea
                    id="bio-title"
                    control={control}
                    name="title"
                  />
                  {errors.title && (
                    <p className="text-sm text-red-500">
                      {errors.title.message}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="bio-ctaButtonText">CTA button text</label>
                  <p className="text-text-tertiary text-sm">
                    The label on your primary CTA button (used in the hero and
                    the closing CTA at the bottom of your profile).
                  </p>
                  <Input
                    id="bio-ctaButtonText"
                    {...register("ctaButtonText")}
                    placeholder="Start a conversation"
                  />
                  {errors.ctaButtonText && (
                    <p className="text-sm text-red-500">
                      {errors.ctaButtonText.message}
                    </p>
                  )}
                </div>
                <CtaActionFields control={control} />
                {isPro && (
                  <div>
                    <label htmlFor="bio-mainVideo">Intro video URL</label>
                    <p className="text-text-tertiary text-sm">
                      Optional. Adds a "Watch intro" link next to your name. Use
                      a YouTube or Vimeo URL.
                    </p>
                    <Input
                      id="bio-mainVideo"
                      {...register("mainVideo")}
                      type="url"
                      placeholder="https://..."
                    />
                    {errors.mainVideo && (
                      <p className="text-sm text-red-500">
                        {errors.mainVideo.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="At a glance"
            description="The small card alongside your hero portrait — plus the trust strip below it (where languages also surface)."
            defaultOpen={false}
            hasError={hasErrorsIn(
              "audienceFor",
              "languages",
              "formOfConduct",
              "coachingStyle",
              "typicalEngagement",
              "coachingSinceYear",
            )}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-audienceFor">Who you work with</label>
                <p className="text-text-tertiary text-sm">
                  Short phrase — one or two audience descriptors. Example:{" "}
                  <em>"Senior corporate leaders"</em> or{" "}
                  <em>"First-time founders"</em>.
                </p>
                <Input
                  id="bio-audienceFor"
                  {...register("audienceFor")}
                  placeholder="Senior corporate leaders"
                />
                {errors.audienceFor && (
                  <p className="text-sm text-red-500">
                    {errors.audienceFor.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="bio-languages">Languages</label>
                <p className="text-text-tertiary text-sm">
                  Languages you can coach in. Shown in the at-a-glance card and
                  the trust strip below the hero.
                </p>
                <Input id="bio-languages" {...register("languages")} />
              </div>
              <FormSelectField
                control={control}
                name="formOfConduct"
                label="Format"
                helpText="How do you coach? Online, in-person, hybrid?"
                options={["Online", "In-person", "Hybrid"]}
              />
              <FormSelectField
                control={control}
                name="coachingStyle"
                label="Coaching style"
                helpText="A few words that describe how you work."
                options={[
                  "Direct and action-oriented",
                  "Structured and goal-focused",
                  "Supportive and encouraging",
                  "Insightful and reflective",
                ]}
              />
              <div>
                <label htmlFor="bio-typicalEngagement">
                  Typical engagement
                </label>
                <p className="text-text-tertiary text-sm">
                  How long clients usually work with you. Example:{" "}
                  <em>"Typically 3–6 months"</em> or <em>"A season"</em>.
                </p>
                <Input
                  id="bio-typicalEngagement"
                  {...register("typicalEngagement")}
                  placeholder="Typically 3–6 months"
                />
                {errors.typicalEngagement && (
                  <p className="text-sm text-red-500">
                    {errors.typicalEngagement.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="bio-coachingSinceYear">Coaching since</label>
                <p className="text-text-tertiary text-sm">
                  The year you started coaching. Shown in the trust strip below
                  your hero as "Coaching since YYYY".
                </p>
                <Input
                  id="bio-coachingSinceYear"
                  type="number"
                  min={1950}
                  max={new Date().getUTCFullYear()}
                  step={1}
                  placeholder="2015"
                  {...register("coachingSinceYear", {
                    setValueAs: (value) =>
                      value === "" || value === null || value === undefined
                        ? null
                        : Number(value),
                  })}
                />
                {errors.coachingSinceYear && (
                  <p className="text-sm text-red-500">
                    {errors.coachingSinceYear.message}
                  </p>
                )}
              </div>
            </CardContent>
          </CollapsibleCard>

          {isPro && (
            <CollapsibleCard
              title="Brand"
              description="Optional. Brand name and logo appear in the footer of your at-a-glance card."
              defaultOpen={false}
              hasError={hasErrorsIn("brandName", "brandLogoUrl")}
            >
              <CardContent className="flex flex-col gap-4">
                <div>
                  <label htmlFor="bio-brandName">Brand name</label>
                  <Input
                    id="bio-brandName"
                    {...register("brandName")}
                    placeholder="Enter your brand name"
                  />
                  {errors.brandName && (
                    <p className="text-sm text-red-500">
                      {errors.brandName.message}
                    </p>
                  )}
                </div>
                <FormFileInputField
                  control={control}
                  name="brandLogoUrl"
                  purpose="brand-logo"
                  label="Brand logo"
                  placeholder="Upload your brand logo"
                  recommendedDimensions={{ width: 96, height: 96 }}
                  accept=".jpg,.jpeg"
                />
              </CardContent>
            </CollapsibleCard>
          )}
        </FormGroup>

        <FormGroup
          label="Profile sections"
          hint="One card per section, in the same order they appear on your profile."
        >
          <CollapsibleCard
            title="01 · About"
            description="Long-form intro paragraph that runs alongside your circular avatar."
            hasError={hasErrorsIn("description")}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-description">Description</label>
                <p className="text-text-tertiary text-sm">
                  Who you are and what you do. Aim for a paragraph or two —
                  visitors are reading this right after your hero.
                </p>
                <ControlledTextarea
                  id="bio-description"
                  control={control}
                  name="description"
                  className="min-h-[200px]"
                  maxLength={400}
                />
                {errors.description && (
                  <p className="text-sm text-red-500">
                    {errors.description.message}
                  </p>
                )}
              </div>
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="02 · Challenges"
            description="What your clients are wrestling with when they come to you — the before-state in their own words."
            hasError={hasErrorsIn(
              "challengesHeadline",
              "challengesDescription",
            )}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-challengesHeadline">Section headline</label>
                <p className="text-text-tertiary text-sm">
                  Optional. Overrides the default "What I help clients work
                  through".
                </p>
                <Input
                  id="bio-challengesHeadline"
                  {...register("challengesHeadline")}
                  placeholder="What I help clients work through"
                />
                {errors.challengesHeadline && (
                  <p className="text-sm text-red-500">
                    {errors.challengesHeadline.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="bio-challengesDescription">
                  Description paragraph
                </label>
                <p className="text-text-tertiary text-sm">
                  The felt experience of being stuck. Empathetic, concrete —
                  sound like the visitor's own internal voice.
                </p>
                <ControlledTextarea
                  id="bio-challengesDescription"
                  control={control}
                  name="challengesDescription"
                  maxLength={400}
                />
                {errors.challengesDescription && (
                  <p className="text-sm text-red-500">
                    {errors.challengesDescription.message}
                  </p>
                )}
              </div>
              <p className="text-text-tertiary border-t border-gray-200 pt-4 text-xs">
                The tiles shown below this paragraph come from the challenges
                you've tagged on{" "}
                <Link
                  className="text-brand-700 underline hover:text-brand-900 hover:no-underline"
                  to="/my-coaching/profile/qualifications"
                >
                  Qualifications
                </Link>
                . Tag more challenges there to broaden your tile coverage and
                appear on more challenge pages.
              </p>
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="03 · Outcomes"
            description="What clients walk away with — concrete results you help them achieve."
            hasError={hasErrorsIn("outcomesHeadline", "outcomesDescription")}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-outcomesHeadline">Section headline</label>
                <p className="text-text-tertiary text-sm">
                  Optional. Overrides the default "What clients walk away with".
                </p>
                <Input
                  id="bio-outcomesHeadline"
                  {...register("outcomesHeadline")}
                  placeholder="What clients walk away with"
                />
                {errors.outcomesHeadline && (
                  <p className="text-sm text-red-500">
                    {errors.outcomesHeadline.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="bio-outcomesDescription">Intro paragraph</label>
                <p className="text-text-tertiary text-sm">
                  Optional. A short paragraph that appears above the result
                  tiles below.
                </p>
                <ControlledTextarea
                  id="bio-outcomesDescription"
                  control={control}
                  name="outcomesDescription"
                  maxLength={400}
                />
                {errors.outcomesDescription && (
                  <p className="text-sm text-red-500">
                    {errors.outcomesDescription.message}
                  </p>
                )}
              </div>
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-900">
                    Result tiles
                  </p>
                  <p className="text-text-tertiary text-sm">
                    Each tile shows an icon, title, and short description. Three
                    per row on desktop.
                  </p>
                </div>
                <Button type="button" onClick={addResult} variant="primary">
                  <Plus /> Add result
                </Button>
                <div className="grid grid-cols-2 gap-4">
                  {results.map((result, index) => (
                    <div
                      key={result.fieldId}
                      className={cn(
                        "flex flex-col gap-2 rounded-xl bg-gray-50 p-4",
                        result._delete && "opacity-50",
                      )}
                    >
                      <input
                        type="hidden"
                        {...register(`results.${index}.id`)}
                      />
                      <div className="flex w-full items-center justify-between gap-2">
                        <FormCheckboxField
                          control={control}
                          name={
                            `results.${index}._delete` as FieldPath<FormData>
                          }
                        >
                          Delete
                        </FormCheckboxField>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveResult(index, index - 1)}
                            disabled={index === 0}
                          >
                            <ArrowUp className="size-[1em]" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveResult(index, index + 1)}
                            disabled={index === results.length - 1}
                          >
                            <ArrowDown className="size-[1em]" />
                          </Button>
                        </div>
                      </div>
                      <FormIconPickerField
                        control={control}
                        name={`results.${index}.icon` as FieldPath<FormData>}
                        label="Icon"
                        required
                        helpText="Pick an icon"
                      />
                      <div>
                        <label htmlFor={`bio-result-${index}-title`}>
                          Title *
                        </label>
                        <Input
                          id={`bio-result-${index}-title`}
                          {...register(`results.${index}.title`)}
                        />
                      </div>
                      <div>
                        <label htmlFor={`bio-result-${index}-description`}>
                          Description
                        </label>
                        <ControlledTextarea
                          id={`bio-result-${index}-description`}
                          control={control}
                          name={
                            `results.${index}.description` as FieldPath<FormData>
                          }
                          maxLength={120}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="04 · Approach"
            description="Why you do this work — your personal motivation."
            defaultOpen={false}
            hasError={hasErrorsIn("whyTitle", "whyContent")}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-whyTitle">Title</label>
                <p className="text-text-tertiary text-sm">
                  A short headline above your story (e.g., "To help one million
                  people…").
                </p>
                <Input
                  id="bio-whyTitle"
                  {...register("whyTitle")}
                  placeholder="eg., To help 1 million people..."
                />
              </div>
              <div>
                <label htmlFor="bio-whyContent">Your story</label>
                <p className="text-text-tertiary text-sm">
                  Personal motivation and origin story — what drew you to this
                  work.
                </p>
                <ControlledTextarea
                  id="bio-whyContent"
                  control={control}
                  name="whyContent"
                  placeholder="Share your personal journey..."
                  rows={6}
                  maxLength={500}
                />
              </div>
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="05 · The coaching"
            description="Your main offer — headline, description, benefits, dates, and price."
            defaultOpen={false}
          >
            <CardContent className="text-text-secondary text-sm">
              Edit your main offer on{" "}
              <Link
                className="text-brand-700 underline hover:text-brand-900 hover:no-underline"
                to="/my-coaching/profile/offers"
              >
                your offer page
              </Link>
              .
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="06 · Credentials"
            description="Training, certifications, and member organizations."
            defaultOpen={false}
          >
            <CardContent className="text-text-secondary text-sm">
              Manage your credentials on{" "}
              <Link
                className="text-brand-700 underline hover:text-brand-900 hover:no-underline"
                to="/my-coaching/profile/qualifications"
              >
                the qualifications page
              </Link>
              .
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="07 · Testimonials"
            description="Real client voices — added under your main offer."
            defaultOpen={false}
          >
            <CardContent className="text-text-secondary text-sm">
              Add testimonials on{" "}
              <Link
                className="text-brand-700 underline hover:text-brand-900 hover:no-underline"
                to="/my-coaching/profile/offers"
              >
                your offer page
              </Link>
              . The first one is featured as a pull-quote band; the rest fill
              the testimonials section.
            </CardContent>
          </CollapsibleCard>

          <CollapsibleCard
            title="08 · Also from me"
            id="lead-magnet"
            description="Lead magnet, articles, and frameworks. Lead magnet is editable here; articles & frameworks come from the editorial system."
            defaultOpen={false}
            hasError={hasErrorsIn(
              "leadMagnetTitle",
              "leadMagnetDescription",
              "leadMagnetCtaText",
              "leadMagnetDownloadCtaText",
              "leadMagnetUrl",
            )}
          >
            <CardContent className="space-y-4">
              <p className="text-text-secondary text-sm">
                The lead magnet renders as a purple tile in the "Also from me"
                section, alongside your latest article and framework if you have
                any.
              </p>
              <div>
                <label htmlFor="bio-leadMagnetTitle">Lead magnet title</label>
                <Input
                  id="bio-leadMagnetTitle"
                  {...register("leadMagnetTitle")}
                  placeholder="3 Keys To A Successful Coaching Business"
                />
              </div>
              <div>
                <label htmlFor="bio-leadMagnetDescription">
                  Description (optional)
                </label>
                <Input
                  id="bio-leadMagnetDescription"
                  {...register("leadMagnetDescription")}
                  placeholder="Download the free report and checklist now"
                />
              </div>
              <div>
                <label htmlFor="bio-leadMagnetCtaText">
                  Submit button text (optional)
                </label>
                <Input
                  id="bio-leadMagnetCtaText"
                  {...register("leadMagnetCtaText")}
                  placeholder="Get Instant Access"
                />
              </div>
              <div>
                <label htmlFor="bio-leadMagnetDownloadCtaText">
                  Download button text (optional)
                </label>
                <Input
                  id="bio-leadMagnetDownloadCtaText"
                  {...register("leadMagnetDownloadCtaText")}
                  placeholder="Download Now"
                />
              </div>
              <div>
                <label htmlFor="bio-leadMagnetUrl">URL</label>
                <Input
                  id="bio-leadMagnetUrl"
                  {...register("leadMagnetUrl")}
                  placeholder="https://..."
                />
              </div>
            </CardContent>
          </CollapsibleCard>
        </FormGroup>

        <FormGroup label="Footer">
          <CollapsibleCard
            title="Find me elsewhere"
            description="Social profiles shown in a discreet row above the closing CTA."
            defaultOpen={false}
            hasError={hasErrorsIn(
              "website",
              "facebookProfile",
              "linkedinProfile",
              "instagramProfile",
              "youtubeChannel",
            )}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-website">Website</label>
                <Input id="bio-website" {...register("website")} />
              </div>
              <div>
                <label htmlFor="bio-facebookProfile">Facebook profile</label>
                <Input
                  id="bio-facebookProfile"
                  {...register("facebookProfile")}
                />
              </div>
              <div>
                <label htmlFor="bio-linkedinProfile">LinkedIn profile</label>
                <Input
                  id="bio-linkedinProfile"
                  {...register("linkedinProfile")}
                />
              </div>
              <div>
                <label htmlFor="bio-instagramProfile">Instagram profile</label>
                <Input
                  id="bio-instagramProfile"
                  {...register("instagramProfile")}
                />
              </div>
              <div>
                <label htmlFor="bio-youtubeChannel">YouTube channel</label>
                <Input
                  id="bio-youtubeChannel"
                  {...register("youtubeChannel")}
                />
              </div>
            </CardContent>
          </CollapsibleCard>
        </FormGroup>

        <FormGroup
          label="Other surfaces"
          hint="Fields used in places other than your profile page."
        >
          <CollapsibleCard
            title="Marketplace listings"
            description="A short quote used on compact listings — featured/founding coach cards, marketplace previews. Not shown on your profile page."
            defaultOpen={false}
            hasError={hasErrorsIn("openingStatement")}
          >
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="bio-openingStatement">Opening statement</label>
                <p className="text-text-tertiary text-sm">
                  One short sentence — punchy, voice-aligned. Used as a quote
                  under your name on listing cards across the marketplace.
                </p>
                <Input
                  id="bio-openingStatement"
                  {...register("openingStatement")}
                />
                {errors.openingStatement && (
                  <p className="text-sm text-red-500">
                    {errors.openingStatement.message}
                  </p>
                )}
              </div>
            </CardContent>
          </CollapsibleCard>
        </FormGroup>

        {!isPro && (
          <CtaBanner
            headline="Bring your profile to the next level"
            description="On to the Pro Plan, you get an upgraded profile page that shows more of your coaching expertise and value."
            buttonHref="/my-coaching/profile/bio"
            buttonText="View membership plans"
            buttonTrack={{
              medium: TrackingMedium.Cta,
              campaign: TrackingCampaign.ProUpgrade,
            }}
          />
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={updateMutation.isPending}
            variant="primary"
          >
            {updateMutation.isPending ? "Saving..." : "Save profile"}
          </Button>
        </div>
      </form>
    </>
  );
}
